import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { getSourceList } from '../../API/sourceDropdown';

/**
 * Dropdown sumber bantuan dengan search + paginate scroll.
 * type: 'questionnaire' | 'verifikasi' | 'registerUsulan'
 * value: { id, label } | null
 */
const SourceDropdown = ({ type, value, onChange, label = 'Pilih sumber data', error, helperText, kecamatan = '', desaKelurahan = '' }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const listboxRef = useRef(null);
  const debounceRef = useRef(null);

  // Reset saat type / filter lokasi berubah
  useEffect(() => {
    setOptions([]);
    setPage(1);
    setTotalPages(1);
    setSearch('');
  }, [type, kecamatan, desaKelurahan]);

  // Fetch data
  const fetchPage = useCallback(async (pageNum, searchTerm, append) => {
    setLoading(true);
    try {
      const res = await getSourceList(type, {
        page: pageNum,
        limit: 20,
        search: searchTerm,
        kecamatan: kecamatan || undefined,
        desaKelurahan: desaKelurahan || undefined,
      });
      setTotalPages(res.totalPages);
      if (append) {
        setOptions((prev) => {
          const ids = new Set(prev.map((o) => o.id));
          return [...prev, ...res.rows.filter((r) => !ids.has(r.id))];
        });
      } else {
        setOptions(res.rows);
      }
    } catch (err) {
      console.error('SourceDropdown fetch error:', err);
      if (!append) setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [type, kecamatan, desaKelurahan]);

  // Search debounce (reset page 1)
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchPage(1, search, false);
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search, open, fetchPage]);

  // Load page 1 saat open / filter lokasi berubah
  useEffect(() => {
    if (open) {
      setPage(1);
      fetchPage(1, search, false);
    }
  }, [open, kecamatan, desaKelurahan]); // eslint-disable-line

  // Scroll handler untuk infinite load
  const handleScroll = (e) => {
    const el = e.currentTarget;
    if (loading) return;
    if (page >= totalPages) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      const next = page + 1;
      setPage(next);
      fetchPage(next, search, true);
    }
  };

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      loading={loading}
      value={value}
      getOptionLabel={(opt) => (opt?.label ?? '')}
      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
      onInputChange={(e, val, reason) => {
        if (reason === 'input') setSearch(val);
      }}
      onChange={(e, val) => onChange(val)}
      filterOptions={(x) => x} // server-side filter
      ListboxProps={{
        ref: listboxRef,
        onScroll: handleScroll,
        style: { maxHeight: 280 },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={error}
          helperText={helperText}
          size="small"
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SourceDropdown;
